import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { Modal, Tooltip } from 'antd';
import moment from 'moment';
import { Api, Notification } from 'services';
import { getMarketSettingsData } from 'actions/studentMarketPlace';
import { COLORS } from 'components/UserDashboardV2/utils/constants';

// SVG Icons matching addStudent.js
const ChevronDownIcon = () => (
    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="#636363" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = () => (
    <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
        <path d="M1.5 5L5 8.5L12.5 1" stroke={COLORS.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CalendarTriggerIcon = () => (
    <svg width="18" height="20" viewBox="0 0 18 20" fill="none">
        <path d="M4 9H14M4 13H10M13 1V5M5 1V5M2.5 3H15.5C16.3284 3 17 3.67157 17 4.5V17.5C17 18.3284 16.3284 19 15.5 19H2.5C1.67157 19 1 18.3284 1 17.5V4.5C1 3.67157 1.67157 3 2.5 3Z" stroke="#636363" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ChevronNavIcon = ({ direction = "left" }) => (
    <svg width="7" height="12" viewBox="0 0 7 12" fill="none" style={{ transform: direction === "right" ? "rotate(180deg)" : "none" }}>
        <path d="M6 1L1 6L6 11" stroke="#191919" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
import VideoCourseIconSvg from './dashboardV3/video-course-icon.svg';
import StudyMaterialsIconSvg from './dashboardV3/study-materials-icon.svg';
import TestSeriesIconSvg from './dashboardV3/test-series-icon.svg';
import LiveCoursesIconSvg from './dashboardV3/live-courses-icon.svg';

const LiveCourseIcon = ({ size = 24 }) => (
    <img src={LiveCoursesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const TestSeriesIcon = ({ size = 24 }) => (
    <img src={TestSeriesIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const VideoCourseIcon = ({ size = 24 }) => (
    <img src={VideoCourseIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const StudyMaterialsIcon = ({ size = 24 }) => (
    <img src={StudyMaterialsIconSvg} alt="" style={{ width: size, height: size, display: "block" }} />
);

const ALL_COURSE_TYPES = [
    { key: "live", label: "Classroom course", Icon: VideoCourseIcon, bg: "#D8F0FF", color: "#008BE3" },
    { key: "video", label: "Video course", Icon: StudyMaterialsIcon, bg: "#DCF2B7", color: "#609900" },
    { key: "test", label: "Test series", Icon: LiveCourseIcon, bg: "#FFEFC5", color: "#D97706" },
    { key: "study", label: "Study materials", Icon: TestSeriesIcon, bg: "#FFDEDF", color: "#DC2626" },
];

const getAvailableCourseTypes = (marketSettings) => {
    if (!marketSettings || Object.keys(marketSettings).length === 0) {
        return [ALL_COURSE_TYPES[0]];
    }
    const settings = marketSettings?.data ?? marketSettings;
    const types = [];
    if (settings?.liveCourseSelected !== false) {
        types.push(ALL_COURSE_TYPES[0]);
    }
    if (settings?.videoResourceSelected) {
        types.push(ALL_COURSE_TYPES[1]);
    }
    if (settings?.onlineTestSelected) {
        types.push(ALL_COURSE_TYPES[2]);
    }
    if (settings?.studyMaterialSelected) {
        types.push(ALL_COURSE_TYPES[3]);
    }
    return types.length > 0 ? types : [ALL_COURSE_TYPES[0]];
};

const fieldLabelStyle = {
    display: 'block',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '14px',
    fontWeight: 500,
    color: '#636363',
    marginBottom: '8px',
};

const requiredMarkStyle = {
    color: '#FF414D',
    marginLeft: '2px',
};

const errorTextStyle = {
    fontFamily: 'Roboto, sans-serif',
    fontSize: '12px',
    color: '#FF414D',
    marginTop: '4px',
};

const inputBoxStyle = (hasError) => ({
    width: '100%',
    height: '48px',
    borderRadius: '12px',
    border: hasError ? '1.5px solid #FF414D' : '1px solid #E6E6E6',
    backgroundColor: '#FFFFFF',
    padding: '0 16px',
    boxSizing: 'border-box',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '15px',
    color: '#191919',
    outline: 'none',
});

const popoverPanelStyle = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    width: '100%',
    maxHeight: '220px',
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    padding: '6px 0',
    zIndex: 30,
    boxSizing: 'border-box',
};

const optionRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    fontFamily: 'Roboto, sans-serif',
    fontSize: '15px',
    cursor: 'pointer',
};

const navButtonStyle = {
    border: 'none',
    background: 'transparent',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

// Nearest ancestor that clips by scrolling (the modal wrapper), falling back
// to the viewport — the area a dropdown/calendar popover has to fit inside.
// Same as addStudent.js.
const getVisibleBounds = (el) => {
    let node = el ? el.parentElement : null;
    while (node && node !== document.body) {
        const { overflowY } = window.getComputedStyle(node);
        if (overflowY === 'auto' || overflowY === 'scroll') {
            const rect = node.getBoundingClientRect();
            return { top: Math.max(rect.top, 0), bottom: Math.min(rect.bottom, window.innerHeight) };
        }
        node = node.parentElement;
    }
    return { top: 0, bottom: window.innerHeight };
};

// Runs before paint once a popover opens: true when it would be cut off
// below (would need a scroll to see) but has more room above — so it opens
// upward instead. Decided only on open. Same as addStudent.js.
const useOpenAbove = (isOpen, anchorRef, panelRef) => {
    const [openAbove, setOpenAbove] = useState(false);
    useLayoutEffect(() => {
        if (!isOpen || !anchorRef.current || !panelRef.current) {
            setOpenAbove(false);
            return;
        }
        const gap = 8;
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const panelHeight = panelRef.current.offsetHeight + gap;
        const bounds = getVisibleBounds(anchorRef.current);
        const spaceBelow = bounds.bottom - anchorRect.bottom;
        const spaceAbove = anchorRect.top - bounds.top;
        setOpenAbove(spaceBelow < panelHeight && spaceAbove > spaceBelow);
    }, [isOpen]);
    return openAbove;
};

// Right chevron while closed, down chevron while open. `searchable` turns
// the box into a text input while open (pre-filled with the selected value,
// cursor at the end) that filters the options by label. `disabled` greys it
// out and blocks opening. Same behaviour as addStudent.js's Dropdown.
// Modal course-type key -> courseType on the student's enrolled courses.
const ENROLLED_TYPE_BY_KEY = { live: 'course', video: 'videoResource', test: 'testSeries', study: 'studyMaterial' };

// Text cut off with "..." — show the full text in a tooltip on hover (only when truncated).
const TruncatedText = ({ text, style }) => {
    const ref = useRef(null);
    const [truncated, setTruncated] = useState(false);
    return (
        <Tooltip title={truncated ? text : ''} placement="topLeft" zIndex={2000}>
            <span
                ref={ref}
                onMouseEnter={() => { const el = ref.current; setTruncated(Boolean(el && el.scrollWidth > el.clientWidth)); }}
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...style }}
            >
                {text}
            </span>
        </Tooltip>
    );
};

const Dropdown = ({ label, required, placeholder, value, options, getLabel, getValue, error, popoverKey, openKey, onOpen, onSelect, onClear, searchable = false, disabled = false, loading = false, emptyText = "No options available" }) => {
    const isOpen = !disabled && openKey === popoverKey;
    const anchorRef = useRef(null);
    const panelRef = useRef(null);
    const openAbove = useOpenAbove(isOpen, anchorRef, panelRef);
    const [searchText, setSearchText] = useState("");
    const searchInputRef = useRef(null);
    const openedWithRef = useRef("");
    const selectedOpt = (options || []).find(o => getValue(o) === value);
    const displayValue = selectedOpt ? getLabel(selectedOpt) : "";

    useLayoutEffect(() => {
        if (!searchable) return;
        if (isOpen) {
            const initial = value ? String(displayValue ?? "") : "";
            openedWithRef.current = initial;
            setSearchText(initial);
            requestAnimationFrame(() => {
                const el = searchInputRef.current;
                if (el) {
                    el.focus();
                    el.setSelectionRange(initial.length, initial.length);
                }
            });
        } else {
            openedWithRef.current = "";
            setSearchText("");
        }
    }, [isOpen]); // displayValue read only at open time

    const query = searchText.trim().toLowerCase();
    const isUnchangedFromOpen = searchText === openedWithRef.current;
    const visibleOptions = searchable && query && !isUnchangedFromOpen
        ? (options || []).filter((o) => String(getLabel(o) ?? "").toLowerCase().includes(query))
        : (options || []);

    return (
        <div data-av2-popover style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
            <label style={fieldLabelStyle}>
                {label}{required && <span style={requiredMarkStyle}>*</span>}
            </label>
            <div
                ref={anchorRef}
                onClick={() => { if (!disabled) onOpen(isOpen ? null : popoverKey); }}
                style={{
                    ...inputBoxStyle(error),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.6 : 1,
                }}
            >
                {searchable && isOpen ? (
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={searchText}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                            // Text fully cleared -> deselect (list stays open).
                            if (!e.target.value && value) onClear?.();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder={placeholder}
                        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontFamily: "Roboto, sans-serif", fontSize: "15px", color: "#191919", padding: 0 }}
                    />
                ) : (
                    <TruncatedText text={value ? displayValue : placeholder} style={{ color: value ? "#191919" : "#B8B8B8" }} />
                )}
                <span style={{ display: "flex", transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform 0.2s ease" }}>
                    <ChevronDownIcon />
                </span>
            </div>
            {error && <div style={errorTextStyle}>This field is required.</div>}
            {isOpen && (
                <div
                    ref={panelRef}
                    style={openAbove
                        // Measured from the input box (not the wrapper, which
                        // includes the label) so the gap matches the 4px below.
                        ? { ...popoverPanelStyle, top: "auto", bottom: `calc(100% - ${anchorRef.current?.offsetTop || 0}px + 4px)` }
                        : popoverPanelStyle}
                >
                    {visibleOptions.length === 0 ? (
                        <div style={{ padding: "10px 12px", fontFamily: "Roboto, sans-serif", fontSize: "14px", color: "#8A8A8A" }}>
                            {loading ? "Loading..." : emptyText}
                        </div>
                    ) : visibleOptions.map((opt, i) => {
                        const optVal = getValue(opt);
                        const optLabel = getLabel(opt);
                        const isSelected = optVal === value;
                        return (
                            <div
                                key={optVal || i}
                                onClick={() => onSelect(optVal)}
                                style={{
                                    ...optionRowStyle,
                                    color: isSelected ? COLORS.primary : "#191919",
                                    fontWeight: isSelected ? 600 : 400,
                                }}
                            >
                                <TruncatedText text={optLabel} />
                                {isSelected && <CheckIcon />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DateField = ({ value, popoverKey, openKey, onOpen, onSelect }) => {
    const isOpen = openKey === popoverKey;
    const [calendarMonth, setCalendarMonth] = useState(() => (value ? moment(value) : moment()));
    const anchorRef = useRef(null);
    const panelRef = useRef(null);
    const openAbove = useOpenAbove(isOpen, anchorRef, panelRef);

    // Every open starts from the selected date's month (today by default),
    // not whatever month it was left on — layout effect so it never flashes.
    useLayoutEffect(() => {
        if (isOpen) {
            setCalendarMonth(value ? moment(value) : moment());
        }
    }, [isOpen]); // value read only at open time

    const cells = useMemo(() => {
        const daysInMonth = calendarMonth.daysInMonth();
        const startOfWeek = calendarMonth.clone().startOf("month").day();
        const c = [];
        for (let i = 0; i < startOfWeek; i++) c.push(null);
        for (let i = 1; i <= daysInMonth; i++) c.push(calendarMonth.clone().date(i));
        return c;
    }, [calendarMonth]);

    return (
        <div data-av2-popover style={{ position: "relative", flex: "1 1 220px", minWidth: 0 }}>
            <label style={fieldLabelStyle}>Enrollment date</label>
            <div
                ref={anchorRef}
                onClick={() => onOpen(isOpen ? null : popoverKey)}
                style={{ ...inputBoxStyle(false), display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
            >
                <span style={{ color: value ? "#191919" : "#B8B8B8" }}>
                    {value ? moment(value).format("DD/MM/YYYY") : "dd/mm/yyyy"}
                </span>
                <CalendarTriggerIcon />
            </div>
            {isOpen && (
                <div ref={panelRef} style={{
                    position: "absolute",
                    // Opens above when it wouldn't fit below (gap measured from
                    // the input box, not the label), else below.
                    ...(openAbove
                        ? { bottom: `calc(100% - ${anchorRef.current?.offsetTop || 0}px + 8px)` }
                        : { top: "calc(100% + 8px)" }),
                    // Left-aligned with the field — right:0 pushed it past the
                    // field's left edge (and the modal) when the field is
                    // narrower than the 340px calendar.
                    left: 0,
                    width: "340px",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "20px",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                    padding: "24px",
                    zIndex: 30,
                    boxSizing: "border-box",
                }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                        <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 600, fontSize: "17px", color: "#191919" }}>
                            {calendarMonth.format("MMMM, YYYY")}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                            <button type="button" onClick={() => setCalendarMonth((m) => m.clone().subtract(1, "month"))} style={navButtonStyle}>
                                <ChevronNavIcon direction="left" />
                            </button>
                            <button type="button" onClick={() => setCalendarMonth((m) => m.clone().add(1, "month"))} style={navButtonStyle}>
                                <ChevronNavIcon direction="right" />
                            </button>
                        </div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: "10px" }}>
                        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                            <span key={d} style={{ textAlign: "center", fontSize: "13px", color: "#8A8A8A", fontFamily: "Roboto, sans-serif", padding: "4px 0" }}>{d}</span>
                        ))}
                        {cells.map((cell, i) => {
                            const isSelected = cell && value && cell.isSame(moment(value), "day");
                            const isToday = cell && cell.isSame(moment(), "day");
                            return (
                                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <div
                                        onClick={() => cell && onSelect(cell.format("YYYY-MM-DD"))}
                                        style={{
                                            width: "36px",
                                            height: "36px",
                                            boxSizing: "border-box",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            cursor: cell ? "pointer" : "default",
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: "15px",
                                            fontWeight: isToday && !isSelected ? 600 : 400,
                                            color: isSelected ? "#FFFFFF" : isToday ? COLORS.primary : "#191919",
                                            backgroundColor: isSelected ? COLORS.primary : "transparent",
                                            border: isToday && !isSelected ? `1.5px solid ${COLORS.primary}` : "none",
                                        }}
                                    >
                                        {cell ? cell.date() : ""}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

// Stop waiting for the roll-number API after this long — the field and
// ENROLL are re-enabled so the user can type a roll number themselves.
const ROLL_FETCH_TIMEOUT_MS = 5000;

// Roll numbers already taken in a batch, from /userv2/student/roll-number's
// `existingRollNumbers` (same check as Add student).
const existingRollsOf = (res) => {
    const list = res?.existingRollNumbers ?? res?.data?.existingRollNumbers;
    return Array.isArray(list) ? list.map((r) => String(r).trim()) : [];
};

// "012" and "12" are the same roll number.
const normalizeRoll = (roll) => {
    const r = String(roll ?? "").trim();
    return /^\d+$/.test(r) ? String(Number(r)) : r.toLowerCase();
};

const PlusIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
        <path d="M12 5V19M5 12H19" stroke={COLORS.primary} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const isLiveType = (type) => !type || type === 'live';

const toCourseTypeParam = (type) => (
    type === 'live' ? 'course' :
        type === 'video' ? 'videoResource' :
            type === 'test' ? 'testSeries' :
                type === 'study' ? 'studyMaterial' : type
);

// One course block — same idea as addStudent.js's courseEntries. Roll-number
// fetches are tagged on the block (rollRequestId), so a late reply is ignored
// once that block's course/batch changed or the modal was reset.
const emptyEntry = (type = 'live') => ({
    type,
    course: '',
    batch: '',
    rollNumber: '',
    // Roll numbers already assigned in the selected batch.
    existingRolls: [],
    enrollmentDate: moment().format('YYYY-MM-DD'),
    rollLoading: false,
    rollRequestId: null,
    rollEdited: false,
});

const EnrollCourseModal = ({
    visible,
    onClose,
    onEnroll,
    userId,
    isBulk = false,
    selectedCount = 0,
    marketSettingData,
    getMarketSettingsData,
    enrolledCourses = [],
    // Unassigned students table only: shows "+ ADD ANOTHER COURSE" and
    // onEnroll gets an array of payloads (one per course block) instead of one.
    allowMultipleCourses = false,
}) => {
    // Always a single block unless allowMultipleCourses.
    const [entries, setEntries] = useState(() => [emptyEntry()]);

    // Fetch market settings when modal is opened so course types match institute settings
    useEffect(() => {
        if (visible) {
            getMarketSettingsData?.(true);
        }
    }, [visible, getMarketSettingsData]);

    const availableCourseTypes = useMemo(() => {
        return getAvailableCourseTypes(marketSettingData);
    }, [marketSettingData]);
    const defaultType = availableCourseTypes[0]?.key || 'live';

    // Ensure each block's type is always one of the available types
    useEffect(() => {
        if (availableCourseTypes.length === 0) return;
        const isAvailable = (t) => availableCourseTypes.some(({ key }) => key === t);
        if (entries.every((e) => isAvailable(e.type))) return;
        setEntries((prev) => prev.map((e) => (
            isAvailable(e.type) ? e : { ...emptyEntry(availableCourseTypes[0].key), enrollmentDate: e.enrollmentDate }
        )));
    }, [availableCourseTypes, entries]);

    // Course lists cached per type, batches per course — fetched immediately
    // (timed=true skips Api's 1s debounce) and kept across modal opens, so the
    // dropdowns have their options as soon as they're opened.
    const [coursesByType, setCoursesByType] = useState({});
    const [batchesByCourse, setBatchesByCourse] = useState({});
    const coursesInFlightRef = useRef({});
    const batchesInFlightRef = useRef({});
    const [openPopover, setOpenPopover] = useState(null);
    // One { course, batch } error object per block.
    const [errors, setErrors] = useState([]);
    const [enrolling, setEnrolling] = useState(false);
    const rollRequestCounterRef = useRef(0);

    // Profile: hide courses the student is already enrolled in (per type).
    const enrolledIdsByType = useMemo(() => {
        const byType = {};
        Object.keys(ENROLLED_TYPE_BY_KEY).forEach((key) => {
            byType[key] = new Set(
                (enrolledCourses || [])
                    .filter(c => (c.courseType === 'live' ? 'course' : (c.courseType || 'course')) === ENROLLED_TYPE_BY_KEY[key])
                    .map(c => String(c.courseId ?? c.id))
            );
        });
        return byType;
    }, [enrolledCourses]);

    const reset = () => {
        // Fresh blocks carry no rollRequestId, so any roll-number reply still
        // in flight is ignored.
        setEntries([emptyEntry(defaultType)]);
        setOpenPopover(null);
        setErrors([]);
        setEnrolling(false);
    };

    const handleClose = () => {
        if (enrolling) return;
        reset();
        onClose();
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest("[data-av2-popover]")) {
                setOpenPopover(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchCoursesForType = (courseType) => {
        if (!courseType || coursesInFlightRef.current[courseType]) return;
        let endpoint = "";
        if (courseType === 'live') endpoint = "/course/filter-course";
        else if (courseType === 'video') endpoint = "/course/filter-video-resource";
        else if (courseType === 'test') endpoint = "/testSeries/filter-test-series";
        else if (courseType === 'study') endpoint = "/course/filter-study-material";
        if (!endpoint) return;
        coursesInFlightRef.current[courseType] = true;
        Api.get(endpoint).params({ disablePagination: true }).send((res) => {
            const raw = res?.data ?? res;
            let items = [];
            if (Array.isArray(raw)) {
                items = raw;
            } else if (raw && typeof raw === 'object') {
                items = raw.videoResources?.items || (Array.isArray(raw.videoResources) ? raw.videoResources : null) ||
                    raw.courses?.items || (Array.isArray(raw.courses) ? raw.courses : null) ||
                    raw.testSeries?.items || (Array.isArray(raw.testSeries) ? raw.testSeries : null) ||
                    raw.studyMaterials?.items || (Array.isArray(raw.studyMaterials) ? raw.studyMaterials : null) ||
                    raw.items || (Array.isArray(raw.data) ? raw.data : []);
            }
            let list = Array.isArray(items) ? items : [];
            // Classroom courses: show only ones that have batches AND chapters/subjects
            // (counts come as strings, e.g. "0"). A missing count is treated as "has some".
            if (courseType === 'live') {
                const has = (v) => v === undefined || v === null || Number(v) > 0;
                list = list.filter((c) => has(c?.batchCount) && (has(c?.chapterCount) || has(c?.subjectCount)));
            }
            setCoursesByType(prev => ({ ...prev, [courseType]: list }));
        }, true);
    };

    // Prefetch course lists for enabled course types once the modal is opened.
    useEffect(() => {
        if (!visible) return;
        availableCourseTypes.forEach(({ key }) => fetchCoursesForType(key));
    }, [visible, availableCourseTypes]);

    // Fetch batches for each block's selected live course (once per course).
    useEffect(() => {
        if (!visible) return;
        entries.forEach(({ type, course }) => {
            if (!course || !isLiveType(type)) return;
            const key = String(course);
            if (batchesByCourse[key] || batchesInFlightRef.current[key]) return;
            batchesInFlightRef.current[key] = true;
            Api.get("/standards").params({ courseId: course }).send((res) => {
                let batches = [];
                if (Array.isArray(res)) {
                    batches = res;
                } else if (res?.data) {
                    if (Array.isArray(res.data)) {
                        batches = res.data;
                    } else if (typeof res.data === 'object') {
                        batches = Object.values(res.data).flat();
                    }
                }
                setBatchesByCourse(prev => ({ ...prev, [key]: batches }));
            }, true);
        });
    }, [visible, entries, batchesByCourse]);

    const patchEntry = (index, patch) => {
        setEntries((prev) => prev.map((e, i) => (i === index ? { ...e, ...patch } : e)));
    };

    const clearError = (index, key) => {
        setErrors((prev) => prev.map((err, i) => (i === index && err ? { ...err, [key]: false } : err)));
    };

    // Drops any roll-number fetch in flight for the block's old selection.
    const cancelledRoll = { existingRolls: [], rollLoading: false, rollRequestId: null };

    const fetchRollNumber = (requestId, standardId) => {
        // Stop blocking the field / ENROLL if the API is slow; a late reply
        // still fills in the roll number (unless the user typed one).
        setTimeout(() => {
            setEntries((current) => current.map((e) => (
                e.rollRequestId === requestId && e.rollLoading ? { ...e, rollLoading: false } : e
            )));
        }, ROLL_FETCH_TIMEOUT_MS);
        // timed=true: send now, not after Api's 1s debounce.
        Api.get('/userv2/student/roll-number').params({ standardId }).send((res) => {
            const autoRoll = res ? (res.nextRollNo ?? res.data?.nextRollNo ?? res.rollNo ?? res.rollNumber ?? res.data?.rollNo ?? res.data?.rollNumber ?? res.data ?? res) : null;
            setEntries((current) => current.map((e) => {
                // A newer batch/course/type change or close superseded this.
                if (e.rollRequestId !== requestId) return e;
                return {
                    ...e,
                    rollLoading: false,
                    rollRequestId: null,
                    ...(res ? { existingRolls: existingRollsOf(res) } : {}),
                    ...(res && !e.rollEdited && autoRoll && typeof autoRoll !== 'object' ? { rollNumber: String(autoRoll) } : {}),
                };
            }));
        }, true);
    };

    const handleTypeChange = (index, newType) => {
        patchEntry(index, { ...cancelledRoll, type: newType, course: '', batch: '' });
        setErrors([]);
        setOpenPopover(null);
    };

    const handleCourseChange = (index, val) => {
        patchEntry(index, { ...cancelledRoll, course: val, batch: '' });
        clearError(index, 'course');
        setOpenPopover(null);
    };

    // Course text cleared in the dropdown: deselect, keep the list open.
    const handleCourseClear = (index) => {
        patchEntry(index, { ...cancelledRoll, course: '', batch: '' });
    };

    const handleBatchChange = (index, val) => {
        // Roll number is only used for a single (non-bulk) enroll.
        if (val && !isBulk) {
            const requestId = ++rollRequestCounterRef.current;
            patchEntry(index, { batch: val, existingRolls: [], rollLoading: true, rollRequestId: requestId, rollEdited: false });
            fetchRollNumber(requestId, val);
        } else {
            patchEntry(index, { batch: val });
        }
        clearError(index, 'batch');
        setOpenPopover(null);
    };

    const handleDateChange = (index, val) => {
        patchEntry(index, { enrollmentDate: val });
        setOpenPopover(null);
    };

    // Each course block's wrapper — used to bring a newly added one into view.
    const entryRefs = useRef([]);
    const scrollToNewEntryRef = useRef(false);

    const addEntry = () => {
        scrollToNewEntryRef.current = true;
        setEntries((prev) => [...prev, emptyEntry(defaultType)]);
    };

    const removeEntry = (index) => {
        setEntries((prev) => prev.filter((_, i) => i !== index));
        setErrors((prev) => prev.filter((_, i) => i !== index));
        setOpenPopover(null);
    };

    // After ADD ANOTHER COURSE: scroll the blocks area so the new
    // "Course N enrollment" heading sits near the top (same as addStudent.js).
    useEffect(() => {
        if (!scrollToNewEntryRef.current) return;
        scrollToNewEntryRef.current = false;
        const el = entryRefs.current[entries.length - 1];
        if (!el) return;
        let parent = el.parentElement;
        while (parent && parent !== document.body) {
            const { overflowY } = window.getComputedStyle(parent);
            if (overflowY === 'auto' || overflowY === 'scroll') break;
            parent = parent.parentElement;
        }
        if (!parent || parent === document.body) return;
        const delta = el.getBoundingClientRect().top - parent.getBoundingClientRect().top - 8;
        parent.scrollBy({ top: delta, behavior: 'smooth' });
    }, [entries.length]);

    // Typed / auto-filled roll number already assigned in this batch (single enroll, Classroom only).
    const isRollTaken = (e) => !isBulk && isLiveType(e.type) && Boolean(normalizeRoll(e.rollNumber))
        && e.existingRolls.some((r) => normalizeRoll(r) === normalizeRoll(e.rollNumber));
    // Wait for the auto-filled roll number (single enroll, live course).
    const isEntryReady = (e) => !!e.course
        && (isBulk || !isLiveType(e.type) || !!e.batch)
        && !(!isBulk && isLiveType(e.type) && e.rollLoading)
        && !isRollTaken(e);
    const canEnroll = entries.every(isEntryReady);

    const handleEnroll = () => {
        const entryErrors = entries.map((e) => ({
            course: !e.course,
            batch: !isBulk && isLiveType(e.type) && !e.batch,
        }));
        if (entryErrors.some((err) => err.course || err.batch)) {
            setErrors(entryErrors);
            return;
        }
        if (entries.some(isRollTaken)) return;

        setEnrolling(true);
        const payloads = entries.map((e) => {
            const isLive = isLiveType(e.type);
            return {
                courseType: toCourseTypeParam(e.type),
                courseId: Number(e.course),
                standardId: isLive ? (e.batch ? Number(e.batch) : undefined) : undefined,
                rollNo: (!isBulk && isLive) ? (e.rollNumber || undefined) : undefined,
                enrollmentDate: e.enrollmentDate ? moment(e.enrollmentDate).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD")
            };
        });

        onEnroll(allowMultipleCourses ? payloads : payloads[0], (success) => {
            setEnrolling(false);
            if (success) {
                reset();
                onClose();
            }
        });
    };

    // More than one block: only the blocks scroll, title and buttons stay put.
    const isScrolling = entries.length > 1;

    return (
        <Modal
            visible={visible}
            title={null}
            footer={null}
            onCancel={handleClose}
            centered
            width={1000}
            className="modal-round-corner"
            bodyStyle={{ padding: "32px" }}
        >
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 600, fontSize: "22px", color: "#191919" }}>
                    {isBulk ? `Assign course${selectedCount ? ` (${selectedCount} student${selectedCount > 1 ? 's' : ''})` : ''}` : 'Course enrollment'}
                </span>

                <div style={isScrolling ? {
                    maxHeight: "calc(100vh - 280px)",
                    overflowY: "auto",
                    // Room for the cards' shadows, which the scroll area would clip.
                    margin: "-4px",
                    padding: "4px",
                } : undefined}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                        {entries.map((entry, index) => {
                            const isLive = isLiveType(entry.type);
                            const entryError = errors[index] || {};
                            const rollTaken = isRollTaken(entry);
                            const enrolledIds = enrolledIdsByType[entry.type] || new Set();
                            // Hide courses already enrolled in, and ones picked in another
                            // block of the same type (keep this block's own selection).
                            const courseOptions = (coursesByType[entry.type] || []).filter((o) => {
                                const optId = String(o?.id);
                                if (String(entry.course) === optId) return true;
                                if (enrolledIds.has(optId)) return false;
                                return !entries.some((other, otherIdx) => (
                                    otherIdx !== index
                                    && other.type === entry.type
                                    && other.course !== ''
                                    && String(other.course) === optId
                                ));
                            });
                            const isCoursesLoading = !coursesByType[entry.type];
                            const batchOptions = (entry.course && isLive && batchesByCourse[String(entry.course)]) || [];
                            const isBatchesLoading = Boolean(entry.course && isLive) && !batchesByCourse[String(entry.course)];
                            const dateField = (
                                <DateField
                                    value={entry.enrollmentDate}
                                    popoverKey={`modal-date-${index}`}
                                    openKey={openPopover}
                                    onOpen={setOpenPopover}
                                    onSelect={(val) => handleDateChange(index, val)}
                                />
                            );
                            return (
                                <div key={index} ref={(node) => { entryRefs.current[index] = node; }} style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                                    {entries.length > 1 && (
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <span style={{ fontFamily: "Roboto, sans-serif", fontWeight: 500, fontSize: "18px", color: "#191919" }}>
                                                {`Course ${index + 1} enrollment`}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => removeEntry(index)}
                                                disabled={enrolling}
                                                style={{ border: "none", background: "transparent", padding: 0, fontFamily: "Roboto, sans-serif", fontWeight: 700, fontSize: "14px", color: "#FF414D", cursor: enrolling ? "not-allowed" : "pointer" }}
                                            >
                                                REMOVE
                                            </button>
                                        </div>
                                    )}

                                    {/* Course Type Cards */}
                                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                        {availableCourseTypes.map(({ key, label, Icon, bg, color }) => {
                                            const selected = entry.type === key;
                                            return (
                                                <div
                                                    key={key}
                                                    onClick={() => handleTypeChange(index, key)}
                                                    style={{
                                                        flex: "1 1 140px",
                                                        minWidth: 0,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "12px",
                                                        padding: "16px",
                                                        borderRadius: "16px",
                                                        cursor: "pointer",
                                                        boxSizing: "border-box",
                                                        backgroundColor: "#FFFFFF",
                                                        border: selected ? `1.5px solid ${COLORS.primary}` : "",
                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                                    }}
                                                >
                                                    <div style={{
                                                        width: "40px", height: "40px", borderRadius: "12px", backgroundColor: bg,
                                                        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                                                    }}>
                                                        <Icon size={20} color={color} />
                                                    </div>
                                                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "15px", fontWeight: 400, color: "#636363", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {label}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Fields */}
                                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                        <Dropdown
                                            label="Course"
                                            required
                                            placeholder="Select course"
                                            value={entry.course}
                                            options={courseOptions}
                                            getLabel={o => o?.name || o?.title || o?.courseName || o?.id}
                                            getValue={o => o?.id}
                                            loading={isCoursesLoading}
                                            emptyText="No courses found"
                                            error={entryError.course}
                                            popoverKey={`modal-course-${index}`}
                                            openKey={openPopover}
                                            onOpen={setOpenPopover}
                                            onSelect={(val) => handleCourseChange(index, val)}
                                            onClear={() => handleCourseClear(index)}
                                            searchable
                                        />
                                        {isLive ? (
                                            <>
                                                <Dropdown
                                                    label="Batch"
                                                    required={!isBulk}
                                                    placeholder="Select batch"
                                                    value={entry.batch}
                                                    options={batchOptions}
                                                    getLabel={o => (o?.std && o?.section) ? `${o.std} - ${o.section}` : (o?.name || o?.standardName || (o?.std || o?.section ? `${o?.std || ''} - ${o?.section || ''}`.trim().replace(/^- |-$/, '') : null) || o?.id)}
                                                    getValue={o => o?.id}
                                                    loading={isBatchesLoading}
                                                    emptyText="No batches found"
                                                    error={!isBulk && entryError.batch}
                                                    popoverKey={`modal-batch-${index}`}
                                                    openKey={openPopover}
                                                    onOpen={setOpenPopover}
                                                    onSelect={(val) => handleBatchChange(index, val)}
                                                    searchable
                                                    // Live course: Batch stays disabled until a course is picked.
                                                    disabled={!entry.course}
                                                />
                                                {!isBulk ? (
                                                    <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                                                        <label style={fieldLabelStyle}>Roll number</label>
                                                        <input
                                                            type="text"
                                                            placeholder={entry.rollLoading ? "Fetching roll number..." : "Enter roll number"}
                                                            value={entry.rollNumber}
                                                            onChange={(e) => patchEntry(index, { rollNumber: e.target.value, rollEdited: true })}
                                                            // Live course: disabled until a course is picked,
                                                            // and while the next roll number is being fetched.
                                                            disabled={!entry.course || entry.rollLoading}
                                                            style={{ ...inputBoxStyle(rollTaken), ...((!entry.course || entry.rollLoading) ? { opacity: 0.6, cursor: "not-allowed" } : {}) }}
                                                        />
                                                        {rollTaken && <div style={errorTextStyle}>Roll number already assigned</div>}
                                                    </div>
                                                ) : dateField}
                                            </>
                                        ) : dateField}
                                    </div>

                                    {isLive && !isBulk && (
                                        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                            {dateField}
                                            <div style={{ flex: "1 1 220px", minWidth: 0 }} />
                                            <div style={{ flex: "1 1 220px", minWidth: 0 }} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {allowMultipleCourses && (
                            <div>
                                <button
                                    type="button"
                                    onClick={addEntry}
                                    disabled={enrolling}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        border: "none",
                                        background: "transparent",
                                        padding: 0,
                                        cursor: enrolling ? "not-allowed" : "pointer",
                                        fontFamily: "Roboto, sans-serif",
                                        fontWeight: 700,
                                        fontSize: "14px",
                                        letterSpacing: "0.4px",
                                        color: COLORS.primary,
                                    }}
                                >
                                    <PlusIcon />
                                    ADD ANOTHER COURSE
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Modal Footer Buttons */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginTop: "12px" }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={enrolling}
                        style={{
                            height: "44px",
                            padding: "0 24px",
                            borderRadius: "12px",
                            border: "1px solid #E6E6E6",
                            backgroundColor: "#FFFFFF",
                            color: "#636363",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "15px",
                            fontWeight: 600,
                            cursor: enrolling ? "not-allowed" : "pointer",
                        }}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        onClick={handleEnroll}
                        disabled={!canEnroll || enrolling}
                        style={{
                            height: "44px",
                            padding: "0 28px",
                            borderRadius: "12px",
                            border: "none",
                            backgroundColor: COLORS.primary,
                            color: "#FFFFFF",
                            fontFamily: "Roboto, sans-serif",
                            fontSize: "15px",
                            fontWeight: 700,
                            cursor: (!canEnroll || enrolling) ? "not-allowed" : "pointer",
                            opacity: (!canEnroll || enrolling) ? 0.6 : 1,
                        }}
                    >
                        {enrolling ? (isBulk ? "ASSIGNING..." : "ENROLLING...") : (isBulk ? "ASSIGN COURSE" : "ENROLL")}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    marketSettingData: state?.studentMarketPlace?.marketSettingData,
});

const mapDispatchToProps = (dispatch) => ({
    getMarketSettingsData: (all, callback) => dispatch(getMarketSettingsData(all, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnrollCourseModal);
